import { MarketplaceAccessSchema, type MarketplaceAccess } from './types';

// SSO base is proxied through nginx (/sso-api → http://48.214.146.211:3001)
// so all requests stay same-origin and HTTPS, avoiding mixed-content blocks.
// VITE_ORBIS_SSO_API_BASE_URL overrides it (backend Marketplace config doc §3).
const SSO_BASE = (
  import.meta.env.VITE_ORBIS_SSO_API_BASE_URL ||
  (typeof window === 'undefined'
    ? 'http://localhost:5173/sso-api'
    : `${window.location.origin}/sso-api`)
).replace(/\/+$/, '');

const APP_URL: string | undefined = import.meta.env.VITE_ORBIS_APP_URL;

export type HttpError = Error & { status: number };

function httpError(path: string, status: number): HttpError {
  const err = new Error(`GET ${path} returned ${status}`) as HttpError;
  err.status = status;
  return err;
}

export interface SsoUser {
  DisplayName: string;
  Email: string | null;
  Username: string | null;
  ObjectId: string;
  TenantId: string;
  Roles: string[];
  Groups: string[];
}

interface SsoTokenResponse {
  AccessToken: string;
  TokenType: string;
  ExpiresIn: number;
}

// ── In-memory token cache ─────────────────────────────────────────────────────
let _cachedToken: string | null = null;
let _tokenExpiry = 0; // epoch ms

export async function fetchMe(): Promise<SsoUser> {
  const res = await fetch(`${SSO_BASE}/me`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw httpError('/me', res.status);
  return res.json() as Promise<SsoUser>;
}

/**
 * Active Marketplace entitlement for the signed-in user's tenant.
 * 200 → entitlement; 401 → not signed in; 403 → no active entitlement.
 *
 * The backend doc says the session cookie is sufficient, but the live endpoint
 * currently answers "Missing bearer token" — so send both until the contract
 * is confirmed.
 */
export async function fetchMarketplaceAccess(): Promise<MarketplaceAccess> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = await getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${SSO_BASE}/marketplace/access`, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
  if (!res.ok) throw httpError('/marketplace/access', res.status);
  return MarketplaceAccessSchema.parse(await res.json());
}

// Returns the cached access token, refreshing it when near expiry (30 s buffer).
export async function getAccessToken(): Promise<string | null> {
  if (_cachedToken && Date.now() < _tokenExpiry - 30_000) return _cachedToken;

  const res = await fetch(`${SSO_BASE}/token`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) return null;

  const data = (await res.json()) as SsoTokenResponse;
  _cachedToken = data.AccessToken;
  _tokenExpiry = Date.now() + data.ExpiresIn * 1_000;
  return _cachedToken;
}

export async function ssoLogout(): Promise<void> {
  _cachedToken = null;
  _tokenExpiry = 0;
  await fetch(`${SSO_BASE}/logout`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
}

// Browser redirect — not a fetch. next defaults to the app root so after the
// Entra callback flow the SSO service lands the user back on the dashboard.
export function startLogin(next = APP_URL || window.location.origin + '/'): void {
  window.location.assign(`${SSO_BASE}/login?next=${encodeURIComponent(next)}`);
}
